import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '@/lib/hooks';
import { Button } from '@/components/ui/button';

export function VerifyEmailPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { verifyEmail } = useAuth();
    const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error'
    const [errorMessage, setErrorMessage] = useState('');
    const redirectTimeoutRef = useRef(null);
    const hasVerifiedRef = useRef(false);

    useEffect(() => {
        // Prevent double verification in StrictMode
        if (hasVerifiedRef.current) return;

        const token = searchParams.get('token');
        if (!token) {
            setStatus('error');
            setErrorMessage('Invalid verification link');
            return;
        }

        hasVerifiedRef.current = true;

        const verify = async () => {
            try {
                await verifyEmail(token);
                setStatus('success');
                // Redirect to home after 2 seconds
                redirectTimeoutRef.current = setTimeout(() => navigate('/'), 2000);
            } catch (err) {
                setStatus('error');
                setErrorMessage(err.message || 'Verification failed');
            }
        };

        verify();

        // Cleanup timeout on unmount
        return () => {
            if (redirectTimeoutRef.current) {
                clearTimeout(redirectTimeoutRef.current);
            }
        };
    }, [searchParams, verifyEmail, navigate]);

    return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4">
            <div className="max-w-md w-full text-center">
                {status === 'verifying' && (
                    <>
                        <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin text-primary" />
                        <h1 className="text-2xl font-bold mb-2">Verifying your email...</h1>
                        <p className="text-muted-foreground">Please wait while we confirm your email address.</p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
                        <h1 className="text-2xl font-bold mb-2">Email verified!</h1>
                        <p className="text-muted-foreground mb-4">
                            Your account has been successfully verified. Redirecting you to the app...
                        </p>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <XCircle className="w-16 h-16 mx-auto mb-4 text-destructive" />
                        <h1 className="text-2xl font-bold mb-2">Verification failed</h1>
                        <p className="text-muted-foreground mb-4">{errorMessage}</p>
                        <Button onClick={() => navigate('/auth')}>
                            Go to Login
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}
